import React, { useMemo } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CertGapResponse } from '../types';
import { CertGapsReport } from './CertGapsReport';
import {
  certGapsBackPath,
  certGapsListApiUrl,
  certGapsTabLabel,
} from './certGapsPrintMeta';

const CertGapsReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const tabRaw = searchParams.get('tab')?.trim() ?? 'gaps';
  const tab = tabRaw === 'ok' || tabRaw === 'all' || tabRaw === 'gaps' ? tabRaw : 'gaps';

  const listUrl = useMemo(() => certGapsListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CertGapResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const rows = useMemo(() => {
    let list = data?.rows ?? [];
    if (tab === 'gaps') list = list.filter((r) => r.status !== 'OK');
    if (tab === 'ok') list = list.filter((r) => r.status === 'OK');
    return [...list].sort((a, b) => {
      const an = (a.name || '').toLowerCase();
      const bn = (b.name || '').toLowerCase();
      if (an !== bn) return an.localeCompare(bn);
      const ac = (a.certificate || '').toLowerCase();
      const bc = (b.certificate || '').toLowerCase();
      if (ac !== bc) return ac.localeCompare(bc);
      return a.crewId - b.crewId || a.certCatalogId - b.certCatalogId;
    });
  }, [data?.rows, tab]);

  const backTo = certGapsBackPath(searchParams);
  const tabLabel = certGapsTabLabel(tab);

  if (!vesselId) {
    return <Navigate to="/crew/cert-gaps" replace />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading Document Gaps…
      </div>
    );
  }

  if (error || !data?.vessel) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load document gaps.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Document Gaps
        </Link>
      </div>
    );
  }

  const subtitle = `${data.vessel.vesselName} · ${tabLabel} · ${rows.length} row${
    rows.length === 1 ? '' : 's'
  }`;

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Document Gaps"
      title="Document Gaps"
      subtitle={subtitle}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <CertGapsReport data={data} rows={rows} tabLabel={tabLabel} />
    </ReportView>
  );
};

export default CertGapsReportPage;
